import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Grid,
    Row,
    Col,
    Table,
    Button,
    Modal,
    FormGroup,
    FormControl,
    ControlLabel,
    Badge,
    Panel,
    InputGroup
} from "react-bootstrap";

import Card from "components/Card/Card.jsx";
import { fetchExperiences, deleteExperience, deleteComment } from '../../../redux/actions/experiences';
import { isAdmin } from 'helpers/permissions';

class AdminExperienceManagement extends Component {
    constructor(props) {
        super(props);
        
        this.state = {
            showDeleteModal: false,
            selectedExperience: null,
            deleteReason: '',
            selectedCommentToDelete: null,
            showCommentDeleteModal: false,
            commentDeleteReason: '',
            searchTerm: '',
            filterStatus: 'all', // all, recent, popular, flagged
            sortBy: 'date', // date, reactions, comments, author
            sortOrder: 'desc' // asc, desc
        };
    }

    componentDidMount() {
        // Charger les expériences au démarrage
        this.props.fetchExperiences();
    }

    handleSearch = (e) => {
        this.setState({ searchTerm: e.target.value });
    };

    handleFilterChange = (filter) => {
        this.setState({ filterStatus: filter });
    };

    handleSort = (sortBy) => {
        const { sortBy: currentSort, sortOrder } = this.state;
        const newOrder = currentSort === sortBy && sortOrder === 'desc' ? 'asc' : 'desc';
        this.setState({ sortBy, sortOrder: newOrder });
    };

    getFilteredAndSortedExperiences = () => {
        const { experiences } = this.props;
        const { searchTerm, filterStatus, sortBy, sortOrder } = this.state;

        let filtered = [...experiences];

        // Filtrer par terme de recherche
        if (searchTerm) {
            filtered = filtered.filter(exp => 
                exp.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                exp.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                exp.author.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filtrer par statut
        switch (filterStatus) {
            case 'recent':
                // Les 24 dernières heures
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                filtered = filtered.filter(exp => new Date(exp.createdAt) > yesterday);
                break;
            case 'popular':
                // Plus de 5 réactions
                filtered = filtered.filter(exp => {
                    const totalReactions = Object.values(exp.reactions || {}).reduce((sum, count) => sum + count, 0);
                    return totalReactions >= 5;
                });
                break;
            case 'flagged':

                filtered = filtered.filter(exp => (exp.comments || []).length >= 10);
                break;
            default:
                break;
        }

        // Trier
        filtered.sort((a, b) => {
            let compareValue = 0;
            
            switch (sortBy) {
                case 'date':
                    compareValue = new Date(a.createdAt) - new Date(b.createdAt);
                    break;
                case 'reactions':
                    const aReactions = Object.values(a.reactions || {}).reduce((sum, count) => sum + count, 0);
                    const bReactions = Object.values(b.reactions || {}).reduce((sum, count) => sum + count, 0);
                    compareValue = aReactions - bReactions;
                    break;
                case 'comments':
                    compareValue = (a.comments || []).length - (b.comments || []).length;
                    break;
                case 'author':
                    compareValue = a.author.name.localeCompare(b.author.name);
                    break;
                default:
                    break;
            }
            
            return sortOrder === 'asc' ? compareValue : -compareValue;
        });

        return filtered;
    };

    handleShowDeleteModal = (experience) => {
        this.setState({
            showDeleteModal: true,
            selectedExperience: experience,
            deleteReason: ''
        });
    };

    handleCloseDeleteModal = () => {
        this.setState({
            showDeleteModal: false,
            selectedExperience: null,
            deleteReason: ''
        });
    };

    handleDeleteExperience = async () => {
        const { selectedExperience, deleteReason } = this.state;
        
        if (!deleteReason.trim()) {
            if (this.props.showNotification) {
                this.props.showNotification("Veuillez indiquer un motif de suppression", "warning");
            }
            return;
        }

        try {
            await this.props.deleteExperience(selectedExperience.id, deleteReason.trim());
            this.handleCloseDeleteModal();
            
            if (this.props.showNotification) {
                this.props.showNotification("Expérience supprimée avec succès", "success");
            }
        } catch (error) {
            if (this.props.showNotification) {
                this.props.showNotification("Erreur lors de la suppression", "error");
            }
        }
    };

    formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    getTotalReactions = (reactions) => {
        if (!reactions) return 0;
        return Object.values(reactions).reduce((total, count) => total + (count || 0), 0);
    };

    getSortIcon = (column) => {
        const { sortBy, sortOrder } = this.state;
        if (sortBy === column) {
            return sortOrder === 'asc' ? '↑' : '↓';
        }
        return '';
    };

    render() {
        const { loading, user, profile } = this.props;
        const {
            showDeleteModal,
            selectedExperience,
            deleteReason,
            searchTerm,
            filterStatus
        } = this.state;

        
        // Vérifier les droits d'admin
        if (!isAdmin(user, profile)) {
            return (
                <div className="content">
                    <Grid fluid>
                        <Row>
                            <Col md={12}>
                                <Card
                                    title="Accès refusé"
                                    content={
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <i className="pe-7s-shield" style={{ fontSize: '48px', color: '#e74c3c' }} />
                                            <h4>Droits d'administrateur requis</h4>
                                            <p>Vous n'avez pas les permissions nécessaires pour accéder à cette section.</p>
                                            <div style={{ 
                                                marginTop: '20px', 
                                                padding: '15px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                color: '#6c757d'
                                            }}>
                                                <strong>Debug Info:</strong><br/>
                                                Email: {user?.email || 'Non défini'}<br/>
                                                Role: {user?.role || 'Non défini'}<br/>
                                                Profile Role: {user?.profile?.role || 'Non défini'}<br/>
                                                UID: {user?.uid || 'Non défini'}
                                            </div>
                                        </div>
                                    }
                                />
                            </Col>
                        </Row>
                    </Grid>
                </div>
            );
        }

        const filteredExperiences = this.getFilteredAndSortedExperiences();

        return (
            <div className="content">
                <Grid fluid>
                    <Row>
                        <Col md={12}>
                            <Card
                                title="Gestion Administrative des Expériences"
                                category="Interface complète de modération et gestion des expériences partagées"
                                content={
                                    <div>
                                        {/* Barre de recherche et filtres */}
                                        <Row style={{ marginBottom: '20px' }}>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <InputGroup>
                                                        <FormControl
                                                            type="text"
                                                            placeholder="Rechercher par titre, contenu ou auteur..."
                                                            value={searchTerm}
                                                            onChange={this.handleSearch}
                                                        />
                                                        <InputGroup.Addon>
                                                            <i className="pe-7s-search" />
                                                        </InputGroup.Addon>
                                                    </InputGroup>
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                    <Button
                                                        bsStyle={filterStatus === 'all' ? 'primary' : 'default'}
                                                        bsSize="sm"
                                                        onClick={() => this.handleFilterChange('all')}
                                                    >
                                                        Toutes
                                                    </Button>
                                                    <Button
                                                        bsStyle={filterStatus === 'recent' ? 'primary' : 'default'}
                                                        bsSize="sm"
                                                        onClick={() => this.handleFilterChange('recent')}
                                                    >
                                                        Récentes (24h)
                                                    </Button>
                                                    <Button
                                                        bsStyle={filterStatus === 'popular' ? 'primary' : 'default'}
                                                        bsSize="sm"
                                                        onClick={() => this.handleFilterChange('popular')}
                                                    >
                                                        Populaires (5+ réactions)
                                                    </Button>
                                                    <Button
                                                        bsStyle={filterStatus === 'flagged' ? 'warning' : 'default'}
                                                        bsSize="sm"
                                                        onClick={() => this.handleFilterChange('flagged')}
                                                    >
                                                        Controversées (10+ commentaires)
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Statistiques rapides */}
                                        <Row style={{ marginBottom: '20px' }}>
                                            <Col md={3}>
                                                <div style={{ 
                                                    textAlign: 'center', 
                                                    padding: '15px',
                                                    backgroundColor: '#3498db',
                                                    color: 'white',
                                                    borderRadius: '8px'
                                                }}>
                                                    <h3 style={{ margin: 0 }}>{this.props.experiences.length}</h3>
                                                    <p style={{ margin: 0, fontSize: '14px' }}>Total Expériences</p>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div style={{ 
                                                    textAlign: 'center', 
                                                    padding: '15px',
                                                    backgroundColor: '#2ecc71',
                                                    color: 'white',
                                                    borderRadius: '8px'
                                                }}>
                                                    <h3 style={{ margin: 0 }}>
                                                        {this.props.experiences.reduce((total, exp) => 
                                                            total + (exp.comments?.length || 0), 0
                                                        )}
                                                    </h3>
                                                    <p style={{ margin: 0, fontSize: '14px' }}>Total Commentaires</p>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div style={{ 
                                                    textAlign: 'center', 
                                                    padding: '15px',
                                                    backgroundColor: '#e74c3c',
                                                    color: 'white',
                                                    borderRadius: '8px'
                                                }}>
                                                    <h3 style={{ margin: 0 }}>
                                                        {this.props.experiences.reduce((total, exp) => 
                                                            total + this.getTotalReactions(exp.reactions), 0
                                                        )}
                                                    </h3>
                                                    <p style={{ margin: 0, fontSize: '14px' }}>Total Réactions</p>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div style={{ 
                                                    textAlign: 'center', 
                                                    padding: '15px',
                                                    backgroundColor: '#f39c12',
                                                    color: 'white',
                                                    borderRadius: '8px'
                                                }}>
                                                    <h3 style={{ margin: 0 }}>
                                                        {new Set(this.props.experiences.map(exp => exp.author.id)).size}
                                                    </h3>
                                                    <p style={{ margin: 0, fontSize: '14px' }}>Auteurs Uniques</p>
                                                </div>
                                            </Col>
                                        </Row>

                                        {loading && (
                                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                                <i className="fa fa-spinner fa-spin" style={{ fontSize: '32px', color: '#3498db' }} />
                                                <p style={{ marginTop: '15px' }}>Chargement des expériences...</p>
                                            </div>
                                        )}

                                        {!loading && filteredExperiences.length === 0 && (
                                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                                <i className="pe-7s-search" style={{ fontSize: '48px', color: '#bdc3c7' }} />
                                                <h4 style={{ color: '#7f8c8d' }}>Aucune expérience trouvée</h4>
                                                <p style={{ color: '#95a5a6' }}>
                                                    {searchTerm ? 'Essayez avec d\'autres termes de recherche.' : 'Aucune expérience ne correspond aux filtres sélectionnés.'}
                                                </p>
                                            </div>
                                        )}

                                        {!loading && filteredExperiences.length > 0 && (
                                            <div>
                                                <div style={{ marginBottom: '15px', fontSize: '14px', color: '#7f8c8d' }}>
                                                    Affichage de {filteredExperiences.length} expérience{filteredExperiences.length > 1 ? 's' : ''} 
                                                    {this.props.experiences.length !== filteredExperiences.length && 
                                                        ` sur ${this.props.experiences.length} au total`
                                                    }
                                                </div>

                                                <Table striped hover responsive>
                                                    <thead>
                                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                            <th 
                                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                onClick={() => this.handleSort('title')}
                                                            >
                                                                Titre {this.getSortIcon('title')}
                                                            </th>
                                                            <th 
                                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                onClick={() => this.handleSort('author')}
                                                            >
                                                                Auteur {this.getSortIcon('author')}
                                                            </th>
                                                            <th 
                                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                onClick={() => this.handleSort('date')}
                                                            >
                                                                Date {this.getSortIcon('date')}
                                                            </th>
                                                            <th 
                                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                onClick={() => this.handleSort('reactions')}
                                                            >
                                                                Réactions {this.getSortIcon('reactions')}
                                                            </th>
                                                            <th 
                                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                onClick={() => this.handleSort('comments')}
                                                            >
                                                                Commentaires {this.getSortIcon('comments')}
                                                            </th>
                                                            <th>Contenu</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredExperiences.map(experience => (
                                                            <tr key={experience.id}>
                                                                <td>
                                                                    <strong style={{ color: '#2c3e50' }}>
                                                                        {experience.title}
                                                                    </strong>
                                                                    {experience.images && experience.images.length > 0 && (
                                                                        <Badge 
                                                                            style={{ 
                                                                                marginLeft: '8px', 
                                                                                backgroundColor: '#f39c12' 
                                                                            }}
                                                                        >
                                                                            {experience.images.length} 📷
                                                                        </Badge>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div style={{ fontSize: '14px' }}>
                                                                        {experience.author.name}
                                                                    </div>
                                                                    <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                                                                        ID: {experience.author.id.substring(0, 8)}...
                                                                    </div>
                                                                </td>
                                                                <td style={{ fontSize: '13px', color: '#7f8c8d' }}>
                                                                    {this.formatDate(experience.createdAt)}
                                                                </td>
                                                                <td>
                                                                    <Badge style={{ backgroundColor: '#e74c3c' }}>
                                                                        {this.getTotalReactions(experience.reactions)}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <Badge style={{ backgroundColor: '#2ecc71' }}>
                                                                        {(experience.comments || []).length}
                                                                    </Badge>
                                                                </td>
                                                                <td style={{ maxWidth: '200px' }}>
                                                                    <div style={{ 
                                                                        fontSize: '13px',
                                                                        color: '#34495e',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {experience.content.substring(0, 100)}
                                                                        {experience.content.length > 100 && '...'}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div style={{ display: 'flex', gap: '5px' }}>
                                                                        <Button
                                                                            bsStyle="info"
                                                                            bsSize="xs"
                                                                            title="Voir détails"
                                                                            onClick={() => {
                                                                            }}
                                                                        >
                                                                            <i className="pe-7s-look" />
                                                                        </Button>
                                                                        <Button
                                                                            bsStyle="danger"
                                                                            bsSize="xs"
                                                                            title="Supprimer"
                                                                            onClick={() => this.handleShowDeleteModal(experience)}
                                                                        >
                                                                            <i className="pe-7s-trash" />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </div>
                                }
                            />
                        </Col>
                    </Row>
                </Grid>

                {/* Modal de suppression d'expérience */}
                <Modal
                    show={showDeleteModal}
                    onHide={this.handleCloseDeleteModal}
                    bsSize="medium"
                >
                    <Modal.Header closeButton>
                        <Modal.Title style={{ color: '#e74c3c' }}>
                            <i className="pe-7s-attention" /> Suppression Administrative
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedExperience && (
                            <div>
                                <div style={{ 
                                    padding: '15px',
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffeaa7',
                                    borderRadius: '4px',
                                    marginBottom: '20px'
                                }}>
                                    <strong>Expérience à supprimer :</strong>
                                    <div style={{ marginTop: '8px' }}>
                                        <strong>"{selectedExperience.title}"</strong>
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#7f8c8d', marginTop: '4px' }}>
                                        Par {selectedExperience.author.name} • {this.formatDate(selectedExperience.createdAt)}
                                    </div>
                                </div>
                                
                                <p style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                                    ⚠️ Cette action est irréversible et l'auteur sera notifié.
                                </p>
                                
                                <FormGroup>
                                    <ControlLabel>Motif de suppression *</ControlLabel>
                                    <FormControl
                                        componentClass="select"
                                        value={deleteReason}
                                        onChange={(e) => this.setState({ deleteReason: e.target.value })}
                                    >
                                        <option value="">Sélectionnez un motif...</option>
                                        <option value="Contenu inapproprié">Contenu inapproprié</option>
                                        <option value="Hors sujet">Hors sujet</option>
                                        <option value="Informations médicales dangereuses">Informations médicales dangereuses</option>
                                        <option value="Spam ou publicité">Spam ou publicité</option>
                                        <option value="Violation des conditions d'utilisation">Violation des conditions d'utilisation</option>
                                        <option value="Signalement par la communauté">Signalement par la communauté</option>
                                        <option value="Autre">Autre motif</option>
                                    </FormControl>
                                </FormGroup>
                                
                                {deleteReason === 'Autre' && (
                                    <FormGroup>
                                        <ControlLabel>Précisez le motif</ControlLabel>
                                        <FormControl
                                            componentClass="textarea"
                                            rows={3}
                                            placeholder="Veuillez préciser le motif de suppression..."
                                            onChange={(e) => this.setState({ deleteReason: e.target.value })}
                                        />
                                    </FormGroup>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.handleCloseDeleteModal}>
                            <i className="pe-7s-close-circle" /> Annuler
                        </Button>
                        <Button 
                            bsStyle="danger"
                            onClick={this.handleDeleteExperience}
                            disabled={!deleteReason}
                        >
                            <i className="pe-7s-trash" /> Supprimer définitivement
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    experiences: state.experiences.experiences,
    loading: state.experiences.loading,
    user: state.user.user || state.user.offlineUser,
    profile: state.user.profile
});

const mapDispatchToProps = dispatch => ({
    fetchExperiences: () => dispatch(fetchExperiences()),
    deleteExperience: (experienceId, reason) => dispatch(deleteExperience(experienceId, reason)),
    deleteComment: (experienceId, commentId, reason) => dispatch(deleteComment(experienceId, commentId, reason))
});

AdminExperienceManagement.propTypes = {
    experiences: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    user: PropTypes.object,
    profile: PropTypes.object,
    fetchExperiences: PropTypes.func.isRequired,
    deleteExperience: PropTypes.func.isRequired,
    deleteComment: PropTypes.func.isRequired,
    showNotification: PropTypes.func
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminExperienceManagement);